import posthog from 'posthog-js';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';

const FeatureFlag = {
  INSIDE_SALES_ADMIN: 'InsideSalesAdmin',
  CRX_ADMIN: 'CRXAdmin',
  PRIMARY_COLOR: 'PrimaryColor',
  AI_FRIEND: 'AIFriend',
  ENABLE_SMART_CONTEXT: 'enableSmartContext',
  AI_FRIEND_CREATE_PERMISSION: 'AIFriendCreatePermission',
  AI_PROMPT_SETTINGS: 'AIPromptSettings',
  STRIPE_HOSTED_CHECKOUT_HYROX: 'isStripeHostedCheckoutHyroxEnabled',
  STRIPE_HOSTED_CHECKOUT_CHALLENGE: 'isStripeHostedCheckoutChallengeEnabled',
  STRIPE_HOSTED_CHECKOUT_AI_FRIEND: 'isStripeHostedCheckoutAiFriendEnabled',
  STRIPE_HOSTED_CHECKOUT_STANDARD_MONTHLY: 'isStripeHostedCheckoutStandardMonthlyEnabled',
  STRIPE_HOSTED_CHECKOUT_DISCOUNTED_FIRST_MONTH: 'isStripeHostedCheckoutDiscountedFirstMonthEnabled',
  REMOVE_PUBLIC_RECIPES: 'removePublicRecipes',
  AI_MEAL_PLAN_BUTTON: 'AIMealPlanDashboardButton',
  AI_WORKOUT_GENERATOR_BUTTON: 'AIWorkoutGeneratorDashboardButton',
  AUTOMATIC_ONBOARDING_MESSAGES: 'AutomaticOnboardingMessages',
  INSTAGRAM_DM: 'InstagramDM',
  INSTAGRAM_AUTOMATION: 'InstagramAutomation',
  ENABLE_LEADS_FORM_MANAGEMENT_FOR_COACHES: 'enableLeadsFormManagementForCoaches',
  ENABLE_COACH_TASKS: 'enableCoachTasks',
  DISENGAGEMENT_THRESHOLDS: 'disengagementThresholds',
  HYROX: 'Hyrox',
  CHALLENGE_PRODUCT_CREATION: 'challengeProductCreation',
  CLIENT_HIGHLIGHT_SHARING: 'clientHighlightSharing',
  M2_VOICE_NOTE: 'M2VoiceNote',
  LONG_WORKOUT_SESSION: 'longWorkoutSession',
  CHALLENGE_CHECKOUT_EXPERIENCE: 'isChallengeCheckoutExperienceEnabled',
  CHALLENGE_UPSELL_PRE_ENROLL: 'isChallengeUpsellPreEnrollEnabled',
  TWO_PAGE_CHECKOUT: 'isTwoPageCheckoutEnabled',
  UPSELL_POPUP_EXPERIMENT: 'isUpsellPopupExperimentEnabled',
  CUSTOM_APP_ICON: 'customAppIcon',
};

const HostedCheckoutType = {
  HYROX: 'HYROX',
  CHALLENGE: 'CHALLENGE',
  AI_FRIEND: 'AI_FRIEND',
  STANDARD_MONTHLY: 'STANDARD_MONTHLY',
  DISCOUNTED_FIRST_MONTH: 'DISCOUNTED_FIRST_MONTH',
};

// Each hosted-checkout type is gated by its own feature flag so they can be
// toggled independently.
const HOSTED_CHECKOUT_FLAG_BY_TYPE = {
  [HostedCheckoutType.HYROX]: FeatureFlag.STRIPE_HOSTED_CHECKOUT_HYROX,
  [HostedCheckoutType.CHALLENGE]: FeatureFlag.STRIPE_HOSTED_CHECKOUT_CHALLENGE,
  [HostedCheckoutType.AI_FRIEND]: FeatureFlag.STRIPE_HOSTED_CHECKOUT_AI_FRIEND,
  [HostedCheckoutType.STANDARD_MONTHLY]: FeatureFlag.STRIPE_HOSTED_CHECKOUT_STANDARD_MONTHLY,
  [HostedCheckoutType.DISCOUNTED_FIRST_MONTH]: FeatureFlag.STRIPE_HOSTED_CHECKOUT_DISCOUNTED_FIRST_MONTH,
};

const isFeatureEnabled = (featureFlag) => posthog.isFeatureEnabled(featureFlag);

const getFeatureFlagPayload = (featureFlag) => posthog.getFeatureFlagPayload(featureFlag);

const isCRXAdmin = () => isFeatureEnabled(FeatureFlag.CRX_ADMIN);

const isInsideSalesAdmin = () => isFeatureEnabled(FeatureFlag.INSIDE_SALES_ADMIN);

const isPrimaryColorEnabled = () => isFeatureEnabled(FeatureFlag.PRIMARY_COLOR);

const isAIFriendEnabled = () => isFeatureEnabled(FeatureFlag.AI_FRIEND);

const isAIFriendCreatePermissionEnabled = () => isFeatureEnabled(FeatureFlag.AI_FRIEND_CREATE_PERMISSION);

const isSmartContextEnabled = () => isFeatureEnabled(FeatureFlag.ENABLE_SMART_CONTEXT);

const isAIPromptSettingsEnabled = () => isFeatureEnabled(FeatureFlag.AI_PROMPT_SETTINGS);

const isAIMealPlanButtonEnabled = () => isFeatureEnabled(FeatureFlag.AI_MEAL_PLAN_BUTTON);

const isAIWorkoutGeneratorEnabled = () => isFeatureEnabled(FeatureFlag.AI_WORKOUT_GENERATOR_BUTTON);

const isEnableLeadsFormManagementForCoaches = () => isFeatureEnabled(
  FeatureFlag.ENABLE_LEADS_FORM_MANAGEMENT_FOR_COACHES,
);

/**
 * Checks if a payload-based feature flag is enabled and contains a specific value.
 * The feature flag payload should be an array.
 * @param {string} featureFlag - The feature flag to check
 * @param {*} value - The value to check against the payload array
 * @returns {boolean} - True if the feature is enabled and the value is in the payload
 */
const isPayloadFeatureEnabled = (featureFlag, value) => {
  if (!isFeatureEnabled(featureFlag)) {
    return false;
  }

  const payload = getFeatureFlagPayload(featureFlag);

  // If no payload is defined, we assume it should be disabled
  if (!payload) {
    return false;
  }

  // Check if the value is in the payload array
  if (value && Array.isArray(payload)) {
    return payload.includes(value);
  }

  return false;
};

const isRemovePublicRecipesEnabled = (coachId) => (
  isPayloadFeatureEnabled(FeatureFlag.REMOVE_PUBLIC_RECIPES, coachId)
);

/**
 * Hook version of payload-based feature flag check.
 * Uses PostHog's native React hooks so the component re-renders when flags load,
 * preventing the "undefined treated as false" race condition.
 *
 * Payload semantics (hook):
 *   - flag disabled → false
 *   - flag enabled + no payload (null / non-array) → false
 *   - flag enabled + empty array payload `[]` → true (open to all — explicit
 *     "no allowlist applied" signal so we can flip to full rollout by clearing
 *     the array in PostHog without code changes)
 *   - flag enabled + populated array payload → true only when `value` is in the array
 *
 * Note: the non-hook `isPayloadFeatureEnabled` above still treats empty array as
 * `false` — it predates this convention and has callers (e.g., `isRemovePublicRecipesEnabled`)
 * that rely on that strict behavior. Don't unify without auditing those callers
 * against their current PostHog payload configs.
 * @param {string} featureFlag - The feature flag to check
 * @param {*} value - The value to check against the payload array
 * @returns {boolean}
 */
const usePayloadFeatureEnabled = (featureFlag, value) => {
  const isEnabled = useFeatureFlagEnabled(featureFlag);
  const payload = useFeatureFlagPayload(featureFlag);

  if (!isEnabled) {
    return false;
  }

  if (!Array.isArray(payload)) {
    return false;
  }

  // Empty array = open to all (deliberate "no allowlist applied" signal)
  if (payload.length === 0) {
    return true;
  }

  return value ? payload.includes(value) : false;
};

const useInstagramDMEnabled = () => useFeatureFlagEnabled(FeatureFlag.INSTAGRAM_DM);

const useInstagramAutomationEnabled = (coachId) => usePayloadFeatureEnabled(FeatureFlag.INSTAGRAM_AUTOMATION, coachId);

const useHyroxEnabled = (coachId) => usePayloadFeatureEnabled(FeatureFlag.HYROX, coachId);

const useChallengeProductCreationEnabled = (userId) => (
  usePayloadFeatureEnabled(FeatureFlag.CHALLENGE_PRODUCT_CREATION, userId)
);

/**
 * Whether a client's content may be turned into a highlight task for this coach.
 *
 * Keyed on the **coach who receives the task**, not the AC composing it: the rollout is per coach, and
 * an AC works across many. So it is the coach that owns the client on screen — from the channel or the
 * /c/:userId route — never the logged-in user.
 *
 * @param {string} [coachId] the coach the highlight task would be delivered to
 */
const useClientHighlightSharingEnabled = (coachId) => (
  usePayloadFeatureEnabled(FeatureFlag.CLIENT_HIGHLIGHT_SHARING, coachId)
);

/**
 * Whether this coach's challenge contracts may pick their checkout experience (Stripe Hosted or Avida-managed).
 *
 * Keyed on the coach, not the visitor: challenge contracts saved before this choice existed already carry
 * `checkoutExperience: 'managed'` as a default, so the allowlist is what keeps their links on Stripe Hosted
 * Checkout until a coach is rolled out deliberately.
 *
 * @param {string} [coachId] the coach who owns the contract
 */
const useChallengeCheckoutExperienceEnabled = (coachId) => (
  usePayloadFeatureEnabled(FeatureFlag.CHALLENGE_CHECKOUT_EXPERIENCE, coachId)
);

/**
 * Whether this coach's challenge clients may pre-enroll into 1:1 coaching at checkout, and whether Upsell Settings
 * shows where admins set that offer up.
 *
 * @param {string} [coachId] the coach who sells the challenge
 */
const useChallengeUpsellPreEnrollEnabled = (coachId) => (
  usePayloadFeatureEnabled(FeatureFlag.CHALLENGE_UPSELL_PRE_ENROLL, coachId)
);

/**
 * Whether Upsell Settings shows where admins set up the test group of the upsell pop-up A/B test for this coach.
 * Only the setup is gated: which group a client is in is the upsellPopupOfferExperiment flag, read by mobile.
 *
 * @param {string} [coachId] the coach whose upsell settings they are
 */
const useUpsellPopupExperimentEnabled = (coachId) => (
  usePayloadFeatureEnabled(FeatureFlag.UPSELL_POPUP_EXPERIMENT, coachId)
);

/**
 * Whether this coach's Avida checkout takes the terms on its first page (a tick box with the terms in a modal) and
 * goes straight to payment, instead of a separate terms step.
 *
 * @param {string} [coachId] the coach whose checkout it is
 */
const useTwoPageCheckoutEnabled = (coachId) => usePayloadFeatureEnabled(FeatureFlag.TWO_PAGE_CHECKOUT, coachId);

const useM2VoiceNotePilotEnabled = (coachId) => {
  const isEnabled = useFeatureFlagEnabled(FeatureFlag.M2_VOICE_NOTE);
  const payload = useFeatureFlagPayload(FeatureFlag.M2_VOICE_NOTE);

  return !!isEnabled && Array.isArray(payload) && payload.includes(coachId);
};

// Whether admins can set a product's own app icon and in-app logo on Products & Pricing.
const useCustomAppIconEnabled = () => useFeatureFlagEnabled(FeatureFlag.CUSTOM_APP_ICON);

export {
  isFeatureEnabled,
  getFeatureFlagPayload,
  isPayloadFeatureEnabled,
  isInsideSalesAdmin,
  isCRXAdmin,
  isPrimaryColorEnabled,
  isAIFriendEnabled,
  isAIFriendCreatePermissionEnabled,
  isSmartContextEnabled,
  isAIPromptSettingsEnabled,
  isRemovePublicRecipesEnabled,
  isAIMealPlanButtonEnabled,
  isAIWorkoutGeneratorEnabled,
  useInstagramDMEnabled,
  useInstagramAutomationEnabled,
  useHyroxEnabled,
  useChallengeProductCreationEnabled,
  useClientHighlightSharingEnabled,
  useChallengeCheckoutExperienceEnabled,
  useChallengeUpsellPreEnrollEnabled,
  useTwoPageCheckoutEnabled,
  useUpsellPopupExperimentEnabled,
  useM2VoiceNotePilotEnabled,
  useCustomAppIconEnabled,
  usePayloadFeatureEnabled,
  FeatureFlag,
  HostedCheckoutType,
  HOSTED_CHECKOUT_FLAG_BY_TYPE,
  isEnableLeadsFormManagementForCoaches,
};
